import { useRouter } from 'next/router';
import sendEvent from '../../helpers/tracking';
import { getString } from '../../helpers/styles';
import React, { useState, useEffect, useRef } from 'react';

import './FileList.scss';

import icons from '../../shared/assets/icons/redesign';
import BackButton from '../BackButton/BackButton.jsx';

const { ChevronLeft, EmailCheck, PhoneCheck, Clip } = icons;

export default function FileList(props) {
  const { consentDossier, user, syncError } = props;

  const iframe = useRef();
  const router = useRouter();

  const list = useRef(null);
  const offsetTop = list.current && list.current.offsetTop;

  const [modifyMode, setModifyMode] = useState(false);

  const [linkKey, setLinkKey] = useState(null);
  const [unlinkKey, setUnlinkKey] = useState(null);

  const [iFrameLoading, setIframeloading] = useState(false);

  const files = props.files.reduce(
    (files, file) => {
      if (file.consentAt) files.linked.push(file);
      else files.unlinked.push(file);

      return files;
    },
    { linked: [], unlinked: [] }
  );

  useEffect(() => {
    if (iframe.current && !modifyMode && user.vistaUrl && props.files.length) {
      setIframeloading(true);

      iframe.current.addEventListener('load', () => setIframeloading(false));
    }
  }, []);

  useEffect(() => {
    if (window?.iFrameResize && iframe.current) {
      window.iFrameResize({ log: false }, '#vista');
    }
  }, [window?.iFrameResize, iframe.current]);

  useEffect(() => {
    // Scroll so that the `Mijn dossiers` header
    // is displayed on the top
    if (offsetTop) window.scrollTo(0, offsetTop - 165);
  }, [modifyMode]);

  useEffect(() => {
    if (offsetTop)
      if (linkKey || unlinkKey) {
        const isMobile = document.body.offsetWidth <= 850;

        // Scroll so that the `Accepteren` button
        // is displayed in the viewport
        if (isMobile) window.scrollTo(0, offsetTop + 1000);
        else window.scrollTo(0, offsetTop - 165);
      } else {
        // Scroll so that the `Mijn dossiers` header
        // is displayed on the top
        window.scrollTo(0, offsetTop - 165);
      }
  }, [linkKey, unlinkKey]);

  if (syncError || !props.files.length)
    return (
      <>
        <button onClick={() => router.push('/dashboard')} className="back-btn">
          <ChevronLeft className="back-btn-icon" />
          Terug
        </button>

        <div className="_fileList-info-message">
          <SupportMessage />
        </div>
      </>
    );

  return (
    <section className="fileList" ref={list}>
      <div className="fileList_header">
        <BackButton />

        {!!props.files.length && !modifyMode && (
          <div className="fileList_header_button">
            <AcceptButton
              filesLength={files.unlinked.length}
              onClick={() => setModifyMode(true)}
            />
          </div>
        )}
      </div>

      {linkKey ? (
        <LinkScreen
          fileKey={linkKey}
          setLinkKey={setLinkKey}
          username={user.username}
          consentDossier={consentDossier}
        />
      ) : unlinkKey ? (
        <UnlinkScreen
          fileKey={unlinkKey}
          username={user.username}
          setUnlinkKey={setUnlinkKey}
          consentDossier={consentDossier}
        />
      ) : (
        <React.Fragment>
          {iFrameLoading && (
            <div className="fileList-iframe-loading">
              <div className="fileList-iframe-loading-string-wrap">
                <div className="fileList-iframe-loading-string">
                  {Array.from({ length: 11 }).map((_, idx) => (
                    <Clip
                      key={idx}
                      className="fileList-iframe-loading-string-icon"
                    />
                  ))}
                </div>

                <svg
                  className="fileList-iframe-loading-icon fileList-iframe-loading-icon-rotate"
                  width="26"
                  height="26"
                  viewBox="0 0 26 26"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13 25C19.6274 25 25 19.6274 25 13C25 6.37258 19.6274 1 13 1C6.37258 1 1 6.37258 1 13C1 19.6274 6.37258 25 13 25Z"
                    stroke="#6A749D"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M10.6351 17.9006H10.9479H18.1429C18.6612 17.9006 19.3942 17.56 19.3942 17.14V11.0555C19.3942 10.6354 18.974 10.2949 18.4557 10.2949H7.19397C6.67566 10.2949 6.25549 10.6354 6.25549 11.0555V17.14C6.25549 17.56 6.67566 17.9006 7.19397 17.9006H10.6351Z"
                    fill="#6A749D"
                  />
                  <path
                    d="M10.9479 17.9006H7.19397C6.67566 17.9006 6.25549 17.56 6.25549 17.14V11.0555C6.25549 10.6354 6.67566 10.2949 7.19397 10.2949H18.4557C18.974 10.2949 19.3942 10.6354 19.3942 11.0555V17.14C19.3942 17.56 18.6612 17.9006 18.1429 17.9006H10.6351"
                    stroke="#6A749D"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M12.8249 14.6896C13.5505 14.6896 14.1387 14.0464 14.1387 13.253C14.1387 12.4596 13.5505 11.8164 12.8249 11.8164C12.0992 11.8164 11.511 12.4596 11.511 13.253C11.511 14.0464 12.0992 14.6896 12.8249 14.6896Z"
                    fill="white"
                  />
                  <path
                    d="M12.7625 16.1078C13.4881 16.1078 13.3756 15.3987 13.3756 14.6053C13.3756 13.8118 13.4881 13.2346 12.7625 13.2346C12.0369 13.2346 12.2369 13.2017 12.2369 13.9951C12.2369 14.7886 12.0369 16.1078 12.7625 16.1078Z"
                    fill="white"
                  />
                  <path
                    d="M18.7811 17.9005C20.8612 17.9005 22.5475 16.2357 22.5475 14.1822C22.5475 12.1286 20.8612 10.4639 18.7811 10.4639C16.7009 10.4639 15.0146 12.1286 15.0146 14.1822C15.0146 16.2357 16.7009 17.9005 18.7811 17.9005Z"
                    fill="white"
                  />
                  <path
                    d="M18.7811 17.9005C20.8612 17.9005 22.5475 16.2357 22.5475 14.1822C22.5475 12.1286 20.8612 10.4639 18.7811 10.4639C16.7009 10.4639 15.0146 12.1286 15.0146 14.1822C15.0146 16.2357 16.7009 17.9005 18.7811 17.9005Z"
                    fill="white"
                  />
                  <path
                    d="M18.781 16.7179C20.1839 16.7179 21.3211 15.5828 21.3211 14.1827C21.3211 12.7825 20.1839 11.6475 18.781 11.6475C17.3781 11.6475 16.2408 12.7825 16.2408 14.1827C16.2408 15.5828 17.3781 16.7179 18.781 16.7179Z"
                    stroke="#6A749D"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M19.7925 13.5068L18.6642 14.6932C18.6134 14.7464 18.5359 14.7797 18.4517 14.7844C18.3675 14.7892 18.2848 14.765 18.2249 14.718L17.9336 14.4883L17.788 14.3734L17.6423 14.2585"
                    stroke="#6A749D"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M9.40881 10.1263C9.40881 8.35277 10.9382 6.91504 12.8249 6.91504C14.7115 6.91504 16.2409 8.35277 16.2409 10.1263"
                    stroke="#6A749D"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>

              <p className="fileList-iframe-loading-text">
                Even even geduld.
                <br />
                We zijn bezig met het opzetten van een veilig verbinding om jouw
                gegevens extra te beschermen...
              </p>
            </div>
          )}

          {modifyMode || !user.vistaUrl ? (
            <FilesLinking
              files={files}
              setLinkKey={setLinkKey}
              setUnlinkKey={setUnlinkKey}
            />
          ) : props.files.length ? (
            <iframe ref={iframe} id="vista" src={user.vistaUrl} />
          ) : (
            <NoDossiersTextBlock />
          )}

          {!!props.files.length && (
            <div className="fileList_buttons">
              {modifyMode ? (
                <button onClick={() => setModifyMode(false)}>
                  Gekoppelde dossiers inzien
                </button>
              ) : (
                <React.Fragment>
                  <button
                    onClick={() => setModifyMode(true)}
                    className="fileList_button_inactive"
                  >
                    Mijn dossiers beheren
                  </button>

                  <AcceptButton
                    filesLength={files.unlinked.length}
                    onClick={() => setModifyMode(true)}
                  />
                </React.Fragment>
              )}
            </div>
          )}
        </React.Fragment>
      )}
    </section>
  );
}

function AcceptButton(props) {
  return (
    <button
      onClick={() => {
        props.onClick();

        sendEvent('ConnectFile', 'Click', 'Accepteerjedossier');
      }}
    >
      Accepteer je dossier(s) ({props.filesLength})
    </button>
  );
}

function Table(props) {
  return (
    <ul
      className={getString([
        {
          value: 'fileList_table',
          condition: true
        },
        {
          value: 'fileList_table_unlinked',
          condition: props.unlinkedEntries
        }
      ])}
    >
      <li>
        <span>Referentie</span>
        <span className="fileList_accept_button">Actie</span>
        <span>Dossier houder</span>
        <span>Zwangerschap</span>
        <span>Client naam</span>
      </li>

      {props.files.map((file) => {
        if (!file.reference) return null;

        return (
          <li key={file.id}>
            <span
              className="fileList_file_reference"
              onClick={() =>
                !props.unlinkedEntries && props.onClick(file.hubResourceKey)
              }
            >
              {file.reference}

              <button className="fileList_mobile" onClick={() => handleClick()}>
                Accepteren
              </button>
            </span>

            <span
              className="fileList_accept_button"
              onClick={() => handleClick()}
            >
              <button>Accepteer dossier</button>
            </span>

            <span>{file.provider?.displayName}</span>
            <span>{file.gravida}</span>
            <span>{file.user && file.user.displayName}</span>
          </li>
        );

        function handleClick() {
          props.onClick(file.hubResourceKey);

          sendEvent('ConnectFile', 'Click', 'Accepteerdossier');
        }
      })}
    </ul>
  );
}

function FilesLinking(props) {
  return (
    <div>
      <h2>Mijn dossiers</h2>

      <div>
        <h3>Nieuw te accepteren dossiers:</h3>

        <p>
          Onderstaande dossiers worden nieuw aan jou beschikbaar gesteld vanuit
          de genoemde dossier houders. Klik op de "Dossier referentie" om jouw
          toestemming te geven om deze op te halen en zelf in te zien.
        </p>

        <Table
          unlinkedEntries={true}
          files={props.files.unlinked}
          onClick={(key) => props.setLinkKey(key)}
        />
      </div>

      <div>
        <h3>Gekoppelde dossiers:</h3>

        <p>
          Onderstaande dossiers heb je eerder gekoppeld. Klik op een dossier om
          de eerder verstrekte toestemming in te trekken.
        </p>

        <Table
          files={props.files.linked}
          onClick={(key) => props.setUnlinkKey(key)}
        />
      </div>
    </div>
  );
}

function LinkScreen(props) {
  return (
    <section className="fileList_linking">
      <p>
        Je bent nog maar één stap verwijderd om een koppeling te maken met jouw
        zwangerschapsdossier. Om dit te doen heeft zwangerenportaal.nl jouw
        toestemming nodig.
      </p>

      <p>
        Door op akkoord te klikken geef Jje zwangerenportaal.nl toestemming om:
      </p>

      <ul>
        <li>
          Jouw zwangerschapsdossier van jouw verloskundige te koppelen met
          zwangerenportaal.nl;
        </li>

        <li>
          Je zwangerschapsduur in jouw profiel gelijk te houden met je dossier;
        </li>

        <li>
          De status van je zwangerschap in jouw profiel gelijk te houden met je
          dossier;
        </li>

        <li>
          Je postcode in jouw profiel gelijk te houden met het dossier.Door deze
          oestemming mag zwangerenportaal.nl met deze gegevens:
          <ul>
            <li>Jouw dossier tonen op jouw zwangerenportaal.nl account;</li>

            <li>
              Jou via e-mail of mobiele app voorzien van relevante informatie
              rondom je zwangerschap;
            </li>

            <li>
              De relevante informatie stoppen of wijzigen wanneer de status van
              jouw zwangerschap wijzigt (bijvoorbeeld in het geval van een
              miskraam of bevalling);
            </li>

            <li>Jou voorzien van relevante en geschikte advertenties.</li>
          </ul>
        </li>
      </ul>

      <p>
        Jouw gegevens zijn veilig bij zwangerenportaal.nl en worden door
        zwangerenportaal.nl nooit gedeeld met derden. Daarnaast kan je altijd:
      </p>

      <ul>
        <li>
          Via het portaal deze toestemming weer intrekken;Je afmelden voor de
          relevante informatie via de e-mails of je profiel instellingen;
        </li>

        <li>
          zwangerenportaal.nl vragen om al jouw gevens weer te verwijderen.
        </li>
      </ul>

      <form
        onSubmit={(e) => {
          linkFile(e, props);

          sendEvent('ConnectFile', 'Click', 'Accepteren');
        }}
      >
        <label>
          <input type="checkbox" required />
          Ja ik geef zwangerenportaal.nl toestemming voor het maken van de
          koppeling, het registreren en gebruiken van mijn zwangerschapsduur,
          status van mijn zwangerschap en postcode.
        </label>

        <div className="fileList_linking_buttons">
          <button onClick={() => props.setLinkKey(null)}>Terug</button>

          <button>Accepteren</button>
        </div>
      </form>

      <PrivacyText />
    </section>
  );
}

function UnlinkScreen(props) {
  return (
    <section className="fileList_linking">
      <p>
        Je hebt ons toestemming gegeven om een koppeling te maken met het
        dossier van je verloskundige of andere behandelaar en/of om je
        zwangerschapsduur te gebruiken. Je hebt het recht om deze toestemming
        altijd in te trekken.
      </p>

      <p>
        Door het intrekken van de toestemming kan je de inhoud van jouw dossier
        niet meer zien. Ook kan het betekenen dat de inhoud van het
        zwangerenportaal verandert. We gebruiken deze informatie namelijk om je
        relevante informatie te tonen en tips te geven of deze te mailen rondom
        je zwangerschap.
      </p>

      <div className="fileList_linking_buttons">
        <button onClick={() => props.setUnlinkKey(null)}>Terug</button>

        <button onClick={() => unlinkFile(props)}>Toestemming intrekken</button>
      </div>

      <PrivacyText />
    </section>
  );
}

function PrivacyText() {
  return (
    <div className="fileList_privacy_text">
      <p>Je gegevens zijn veilig bij ons.</p>

      <p>
        We nemen je privacy serieus en voldoen altijd aan de wettelijke
        bepalingen hiervoor. Ook nemen we maatregelen om jouw gegevens te
        beschermen. Wil je weten hoe we met je privacy omgaan? Lees dan onze{' '}
        <a href="/privacyverklaring" target="_blank">
          privacy verklaring
        </a>
        .
      </p>
    </div>
  );
}

function NoDossiersTextBlock() {
  return (
    <React.Fragment>
      <h2>
        Er zijn nog geen zwangerschapdossiers met jou gedeeld om te accepteren.
      </h2>

      <p>
        Om jouw zwangerschapsdossier in zwangerenportaal.nl te kunnen tonen moet
        dit gedeeld worden door de verloskundigen/zorgverleners. Neem hiervoor
        dus contact op met je verloskundig/zorgverlener.
      </p>

      <p>Wanneer jouw zorgverleners hun dossier hebben gedeeld kan je:</p>

      <ul>
        <li>
          Alle dossiers bekijken die jouw zorgverleners met zwangerenportaal.nl
          hebben gedeeld;
        </li>

        <li>
          Je echobeelden bekijken van alles zorgverleners die deze hebben
          gedeeld zwangerenportaal.nl;
        </li>

        <li>
          Afspraakbevestigingen- en herinneringen ontvangen met alle informatie
          voor je afspraak;
        </li>

        <li>
          Videoconsulten voeren met je verloskundige wanneer het niet mogelijk
          is om langs te komen op de praktijken.
        </li>
      </ul>

      <p>
        Wanneer jouw dossier gedeeld is door de verloskundigen/zorgverleners kun
        je hier je dossiers accepteren.
      </p>
    </React.Fragment>
  );
}

function linkFile(e, props) {
  e.preventDefault();

  props.consentDossier({
    variables: {
      dossierKey: props.fileKey,
      consent: true
    }
  });

  props.setLinkKey(null);
}

function unlinkFile(props) {
  props.consentDossier({
    variables: {
      dossierKey: props.fileKey,
      consent: false
    }
  });

  props.setUnlinkKey(null);
}

function SupportMessage() {
  return (
    <div className="support-message">
      <p>
        <span className="bold">
          We kunnen op dit moment nog geen dossier vinden.
        </span>
        <br />
        Dit kan komen doordat de koppeling nog wordt verwerkt of omdat jouw
        gegevens niet overeenkomen met de gegevens in het dossier van je
        verloskundige. Controleer en pas je gegevens eenvoudig aan via je
        profiel, zodat je verloskundige je kan terugvinden in haar systeem.
      </p>

      <p>
        <span className="bold">Tip:</span> Bij een nieuwe zwangerschap kan er
        een nieuw dossier zijn dat opnieuw gekoppeld of gedeeld moet worden.
      </p>

      <ul>
        <li>
          Blijft het dossier langer dan 3 dagen onzichtbaar en kloppen je
          gegevens? Neem dan contact op met je verloskundige via de
          contactgegevens van de praktijk.
        </li>
        <li>
          Ervaar je problemen met je account op zwangerenportaal.nl? Neem dan
          contact met ons op via het contactformulier. We helpen je graag verder.
        </li>
      </ul>
    </div>
  );
}
